import type {
	CoursesAdminCreateLessonDto,
	CoursesAdminCreateModuleDto,
	CoursesAdminReorderIdsDto,
	CoursesAdminSaveQuizDto,
	CoursesAdminUpdateLessonDto,
	CoursesAdminUpdateModuleDto,
} from '@sportspredict/shared';
import type { Wire } from '../../types/common.types';
import { coursesApi } from './courses-base.api';
import type {
	ListModulesAdminOutput,
	CreateModuleInput,
	CreateModuleOutput,
	UpdateModuleInput,
	UpdateModuleOutput,
	DeleteModuleInput,
	DeleteModuleOutput,
	ReorderModulesInput,
	ReorderModulesOutput,
	GetModuleAdminInput,
	GetModuleAdminOutput,
	CreateLessonInput,
	CreateLessonOutput,
	DeleteLessonInput,
	DeleteLessonOutput,
	ReorderLessonsInput,
	ReorderLessonsOutput,
	GetLessonAdminInput,
	GetLessonAdminOutput,
	UpdateLessonInput,
	UpdateLessonOutput,
	SaveQuizInput,
	SaveQuizOutput,
	CreateLessonFileUploadUrlInput,
	CreateLessonFileUploadUrlOutput,
	CreateLessonImageUploadUrlInput,
	CreateLessonImageUploadUrlOutput,
	ConfirmLessonFileInput,
	ConfirmLessonFileOutput,
} from '../../types/admin-modules.types';

const TAG = 'courses:admin-modules' as const;
const INVALIDATES = [
	TAG,
	'courses:courses',
	'courses:modules',
	'courses:lessons',
	'courses:dashboard',
] as const;

export const adminModulesEndpoints = coursesApi.injectEndpoints({
	endpoints: (builder) => ({
		listModulesAdmin: builder.query<ListModulesAdminOutput, void>({
			query: () => ({ url: 'courses/admin/modules' }),
			providesTags: [TAG],
		}),
		createModule: builder.mutation<CreateModuleOutput, CreateModuleInput>({
			query: (body) => ({
				url: 'courses/admin/modules',
				method: 'POST',
				body: body satisfies Wire<CoursesAdminCreateModuleDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		updateModule: builder.mutation<UpdateModuleOutput, UpdateModuleInput>({
			query: ({ id, ...body }) => ({
				url: `courses/admin/modules/${encodeURIComponent(id)}`,
				method: 'PATCH',
				body: body satisfies Wire<CoursesAdminUpdateModuleDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		deleteModule: builder.mutation<DeleteModuleOutput, DeleteModuleInput>({
			query: ({ id }) => ({
				url: `courses/admin/modules/${encodeURIComponent(id)}`,
				method: 'DELETE',
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		reorderModules: builder.mutation<ReorderModulesOutput, ReorderModulesInput>({
			query: (body) => ({
				url: 'courses/admin/modules/reorder',
				method: 'POST',
				body: body satisfies Wire<CoursesAdminReorderIdsDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		getModuleAdmin: builder.query<GetModuleAdminOutput, GetModuleAdminInput>({
			query: ({ moduleId }) => ({ url: `courses/admin/modules/${encodeURIComponent(moduleId)}` }),
			providesTags: [TAG],
		}),
		createLesson: builder.mutation<CreateLessonOutput, CreateLessonInput>({
			query: ({ moduleId, ...body }) => ({
				url: `courses/admin/modules/${encodeURIComponent(moduleId)}/lessons`,
				method: 'POST',
				body: body satisfies Wire<CoursesAdminCreateLessonDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		deleteLesson: builder.mutation<DeleteLessonOutput, DeleteLessonInput>({
			query: ({ id }) => ({
				url: `courses/admin/modules/lessons/${encodeURIComponent(id)}`,
				method: 'DELETE',
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		reorderLessons: builder.mutation<ReorderLessonsOutput, ReorderLessonsInput>({
			query: ({ moduleId, ...body }) => ({
				url: `courses/admin/modules/${encodeURIComponent(moduleId)}/lessons/reorder`,
				method: 'POST',
				body: body satisfies Wire<CoursesAdminReorderIdsDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		getLessonAdmin: builder.query<GetLessonAdminOutput, GetLessonAdminInput>({
			query: ({ lessonId }) => ({
				url: `courses/admin/modules/lessons/${encodeURIComponent(lessonId)}`,
			}),
			providesTags: [TAG],
		}),
		updateLesson: builder.mutation<UpdateLessonOutput, UpdateLessonInput>({
			query: ({ id, ...body }) => ({
				url: `courses/admin/modules/lessons/${encodeURIComponent(id)}`,
				method: 'PATCH',
				body: body satisfies Wire<CoursesAdminUpdateLessonDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		saveQuiz: builder.mutation<SaveQuizOutput, SaveQuizInput>({
			query: ({ lessonId, ...body }) => ({
				url: `courses/admin/modules/lessons/${encodeURIComponent(lessonId)}/quiz`,
				method: 'POST',
				body: body satisfies Wire<CoursesAdminSaveQuizDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		createLessonFileUploadUrl: builder.mutation<
			CreateLessonFileUploadUrlOutput,
			CreateLessonFileUploadUrlInput
		>({
			query: ({ lessonId, ...body }) => ({
				url: `courses/admin/modules/lessons/${encodeURIComponent(lessonId)}/file/upload-url`,
				method: 'POST',
				body,
			}),
		}),
		createLessonImageUploadUrl: builder.mutation<
			CreateLessonImageUploadUrlOutput,
			CreateLessonImageUploadUrlInput
		>({
			query: ({ lessonId, ...body }) => ({
				url: `courses/admin/modules/lessons/${encodeURIComponent(lessonId)}/images/upload-url`,
				method: 'POST',
				body,
			}),
		}),
		confirmLessonFile: builder.mutation<ConfirmLessonFileOutput, ConfirmLessonFileInput>({
			query: ({ lessonId, ...body }) => ({
				url: `courses/admin/modules/lessons/${encodeURIComponent(lessonId)}/file/confirm`,
				method: 'POST',
				body,
			}),
			invalidatesTags: ['courses:admin-modules', 'courses:lessons'],
		}),
	}),
});

export const {
	useListModulesAdminQuery,
	useLazyListModulesAdminQuery,
	useCreateModuleMutation,
	useUpdateModuleMutation,
	useDeleteModuleMutation,
	useReorderModulesMutation,
	useGetModuleAdminQuery,
	useLazyGetModuleAdminQuery,
	useCreateLessonMutation,
	useDeleteLessonMutation,
	useReorderLessonsMutation,
	useGetLessonAdminQuery,
	useLazyGetLessonAdminQuery,
	useUpdateLessonMutation,
	useSaveQuizMutation,
	useCreateLessonFileUploadUrlMutation,
	useCreateLessonImageUploadUrlMutation,
	useConfirmLessonFileMutation,
} = adminModulesEndpoints;
